import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Form, Input, Button, Typography, ConfigProvider, Alert, message, Checkbox, Modal } from 'antd';
import { ArrowRightOutlined, BankOutlined, MailOutlined, PhoneOutlined, UserOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import authActions from '../../actions/authActions';
import AnimatedDrivingScene from '../../components/svg/animatedDrivingScene';
import ConfirmModal from '../../components/confirmModal';
import { TERMS_AND_CONDITIONS } from '../../actions/constants/constant';
import { getblobLink } from '../../services/helper';

const { Title, Text } = Typography;

const Register: React.FC = () => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const registerState = useSelector((state: any) => state.registerTenantReducer);
  const checkState = useSelector((state: any) => state.checkTenantExistsReducer);
  const [status, setStatus] = useState<{ type: 'success' | 'error' | null; text?: string }>({ type: null });
  const [nameExists, setNameExists] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [phoneNumberExists, setPhoneExists] = useState(false);
  const [debounceTimers, setDebounceTimers] = useState<{ name?: any; email?: any; phoneNumber?: any }>({});
  const lastCheckedField = useRef<'name' | 'email' | 'phoneNumber' | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [tempAccepted, setTempAccepted] = useState(false);
  const [termsScrolledToEnd, setTermsScrolledToEnd] = useState(false);

  useEffect(() => {
    if (!isTermsOpen) return;
    setTimeout(() => {
      const el = document.getElementById('terms-scroll-container') as HTMLElement | null;
      if (el) {
        el.scrollTop = 0;
        setTermsScrolledToEnd(false);
      }
    }, 0);
  }, [isTermsOpen]);

  const selectedPlan = useMemo(() => {
    const plan = searchParams.get('plan');
    const billing = searchParams.get('billing');
    return plan ? { name: plan, billing: billing || 'monthly' } : null;
  }, [searchParams]);

  const onFinish = (values: any) => {
    if (nameExists || emailExists || phoneNumberExists) return;
    if (!agreed) {
      message.error('Please agree to the Terms and Conditions before registering.');
      return;
    }
    dispatch(authActions.registerTenantRequest(values));
  };

  useEffect(() => {
    if (registerState?.isSuccess) {
      setStatus({ type: 'success', text: 'Registration successful! Please check your email to verify your account.' });
      form.resetFields();
      setTimeout(() => {
        if (selectedPlan) {
          message.info('Please login to complete your subscription.');
          navigate(`/login?redirect=pricing&plan=${selectedPlan.name}&billing=${selectedPlan.billing}`);
        } else {
          navigate('/login');
        }
      }, 2000);
      registerState.isSuccess = false; // Reset success state to prevent repeated navigation
    }
    if (registerState?.isError) {
      setStatus({ type: 'error', text: registerState?.error || 'Registration failed. Please try again.' });
    }
  }, [registerState, navigate, selectedPlan, form]);

  useEffect(() => {
    if (checkState?.isSuccess && checkState?.data) {
      const field = lastCheckedField.current;
      if (field === 'name') setNameExists(!!checkState.data.nameExists);
      else if (field === 'email') setEmailExists(!!checkState.data.emailExists);
      else if (field === 'phoneNumber') setPhoneExists(!!checkState.data.phoneNumberExists);
    }
  }, [checkState?.isSuccess, checkState?.data]);

  const checkExists = useCallback((field: 'name' | 'email' | 'phoneNumber', value: string) => {
    if (!value || value.trim().length < 3) return;
    if (field === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return;
    if (field === 'phoneNumber' && !/^\d{10}$/.test(value)) return;
    if (debounceTimers[field]) clearTimeout(debounceTimers[field]);

    const timer = setTimeout(() => {
      lastCheckedField.current = field;
      dispatch(authActions.checkTenantExistsRequest({ [field]: value.trim() }));
    }, 500);

    setDebounceTimers(prev => ({ ...prev, [field]: timer }));
  }, [dispatch, debounceTimers]);

  const handleCheckboxClick = () => {
    if (!agreed) {
      setIsTermsOpen(true);
      setTempAccepted(false);
      setTermsScrolledToEnd(false);
    } else {
      setAgreed(false);
    }
  };

  const handleAcceptTerms = () => {
    if (!termsScrolledToEnd) {
      return;
    } else {
      setAgreed(true);
      setIsTermsOpen(false);
    }
  };

  const handleCancelTerms = () => {
    setIsTermsOpen(false);
    setTempAccepted(false);
    setTermsScrolledToEnd(false);
  };

  return (
    <ConfigProvider theme={{ token: { colorPrimary: '#4F86F7', borderRadius: 8, colorTextBase: '#1f2937' } }}>
      <div className="split-layout-wrapper">
        <div className="cursor-pointer h-fit ml-[70px] mt-[40px]" onClick={() => { navigate("/") }} >
          <img src={`${getblobLink()}/public/asset/logo_with_name.png`} alt="DriveMynt Logo"
          loading="lazy" width="180" height="180"/>
        </div>

        <div className="split-left">
          <AnimatedDrivingScene
            title="Start Your Journey"
            subtitle="Register your driving school and manage everything from students to vehicles — all in one platform."
          />
        </div>
        <div className="split-right">
          <div className="auth-bg-decoration">
            <div className="floating-shape shape-1"></div>
            <div className="floating-shape shape-2"></div>
            <div className="floating-shape shape-3"></div>
            <div className="floating-shape shape-4"></div>
          </div>

          <div className="auth-form-container">
            <div className="auth-form-content">
              <Title level={2} className="auth-title">Create Your Account</Title>
              <Text className="auth-subtitle left-align">
                Register your driving school to get started
              </Text>
              {selectedPlan && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <Text strong className="text-blue-900">Selected Plan: {selectedPlan.name}</Text>
                      <Text className="block text-sm text-blue-700">Billing: {selectedPlan.billing === 'yearly' ? 'Yearly (Save 20%)' : 'Monthly'}</Text>
                    </div>
                    <Button type="link" size="small" onClick={() => navigate('/pricing')}>Change</Button>
                  </div>
                </div>
              )}
             
              <Form
                form={form}
                name="register_form"
                layout="vertical"
                className="auth-form"
                requiredMark={false}
                onFinish={onFinish}
              >
                <Form.Item
                  name="name"
                  label="Driving School Name"
                  validateStatus={nameExists ? 'error' : ''}
                  help={nameExists ? 'A driving school with this name already exists' : undefined}
                  rules={[
                    { required: true, message: 'Please enter your driving school name' },
                    { min: 3, message: 'Name must be at least 3 characters' },
                  ]}
                >
                  <Input
                    placeholder="Enter your driving school name"
                    size="large"
                    className='register-input'
                    prefix={<BankOutlined style={{ color: '#5B8DEF' }} />}
                    onChange={(e) => {
                      setNameExists(false);
                      checkExists('name', e.target.value);
                    }}
                  />
                </Form.Item>

                <Form.Item
                  name="email"
                  label="Email Address"
                  validateStatus={emailExists ? 'error' : ''}
                  help={emailExists ? 'An account with this email already exists' : undefined}
                  rules={[
                    { required: true, message: 'Please enter your email address' },
                    { type: 'email', message: 'Please enter a valid email address' },
                  ]}
                >
                  <Input
                    placeholder="Enter your email address"
                    size="large"
                    className='register-input'
                    prefix={<MailOutlined style={{ color: '#5B8DEF' }} />}
                    onChange={(e) => {
                      setEmailExists(false);
                      checkExists('email', e.target.value);
                    }}
                  />
                </Form.Item>

                <Form.Item
                  name="phoneNumber"
                  label="Phone Number"
                  validateStatus={phoneNumberExists ? 'error' : ''}
                  help={phoneNumberExists ? 'An account with this phone number already exists' : undefined}
                  rules={[
                    { required: true, message: 'Please enter your phone number' },
                    { pattern: /^\d{10}$/, message: 'Enter a valid 10-digit mobile number' },
                  ]}
                >
                  <Input
                    placeholder="Enter your mobile number"
                    size="large" className='register-input'
                    prefix={<PhoneOutlined style={{ color: '#5B8DEF' }} />}
                    maxLength={10}
                    onKeyPress={(e) => {
                      if (!/^\d$/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onChange={(e) => {
                      setPhoneExists(false);
                      checkExists('phoneNumber', e.target.value);
                    }}
                  />
                </Form.Item>

                <div style={{ display: 'flex', gap: 12 }}>
                  <Form.Item
                    name="firstName"
                    label="First Name"
                    style={{ flex: 1 }}
                    rules={[{ required: true, message: 'Please enter first name' }]}
                  >
                    <Input
                      placeholder="First name"
                      size="large"
                      className='register-input'
                      prefix={<UserOutlined style={{ color: '#5B8DEF' }} />}
                    />
                  </Form.Item>

                  <Form.Item
                    name="lastName"
                    label="Last Name"
                    style={{ flex: 1 }}
                    rules={[{ required: true, message: 'Please enter last name' }]}
                  >
                    <Input
                      placeholder="Last name"
                      size="large"
                      className='register-input'
                    />
                  </Form.Item>
                </div>

                <Form.Item>
                  <Checkbox checked={agreed} onChange={handleCheckboxClick}>
                    I agree to the&nbsp;
                    <a onClick={() => setIsTermsOpen(true)} style={{ cursor: 'pointer' }}>
                      Terms & Conditions
                    </a>
                  </Checkbox>
                </Form.Item>

                <Form.Item style={{ marginBottom: 20 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    block
                    loading={registerState?.isLoading}
                    disabled={nameExists || emailExists || phoneNumberExists || !agreed}
                    className="auth-submit-btn"
                    icon={<ArrowRightOutlined />}
                    iconPosition="end"
                  >
                    Register
                  </Button>
                </Form.Item>

                <div className="auth-legal">
                  <Text className="auth-legal-text">
                    Already have an account?&nbsp;
                    <a
                      href={selectedPlan
                        ? `/login?redirect=pricing&plan=${encodeURIComponent(selectedPlan.name)}&billing=${selectedPlan.billing}`
                        : '/login'
                      }
                      className="link-primary"
                    >
                      Sign in
                    </a>
                  </Text>
                </div>
              </Form>

              <ConfirmModal
                title="Terms & Conditions"
                visible={isTermsOpen}
                onOk={handleAcceptTerms}
                onCancel={handleCancelTerms}
                okText={"Accept"}
                cancelText="Cancel"
                okType="primary"
                okButtonProps={{ disabled: !termsScrolledToEnd }}
                width={760}
                content={
                  <div>
                    <div id="terms-scroll-container" style={{
                      maxHeight: '50vh', overflowY: 'auto',
                      marginBottom: 20, padding: '16px', backgroundColor: '#f9fafb', borderRadius: 8
                    }}
                      onScroll={(e) => {
                        const target = e.target as HTMLElement;
                        const isEnd = target.scrollTop + target.clientHeight >= target.scrollHeight - 8;
                        if (isEnd) setTermsScrolledToEnd(true);
                      }}
                    >
                      <div dangerouslySetInnerHTML={{ __html: TERMS_AND_CONDITIONS }} />
                    </div>
                  </div>}
              />
            </div>
          </div>
        </div>
      </div>
    </ConfigProvider>
  );
};

export default Register;
